import type { InferInput } from 'valibot'
import { number, object, omit, optional, parse, picklist, pipe, safeParser, string, transform, union } from 'valibot'

export const paginationSchema = object({
  page: union([
    optional(number(), 1),
    optional(pipe(string(), transform(val => Number.parseInt((val ?? '1'), 10)))),
  ]),
  limit: union([
    optional(number(), 10),
    optional(pipe(string(), transform(val => Number.parseInt((val ?? '10'), 10)))),
  ]),
  sort_by: optional(string(), 'createdAt'),
  order: optional(picklist(['asc', 'desc']), 'desc'),
})

export type PaginationType = InferInput<typeof paginationSchema>

export const requiredPaginationSchema = omit(paginationSchema, ['sort_by', 'order'])
export type RequiredPaginationType = InferInput<typeof requiredPaginationSchema>

export const defaultPaginationSchema = object({
  page: optional(number(), 1),
  limit: optional(number(), 10),
})

const MAX_PAGINATION_LIMIT = 100

export function getPaginationParams(reqQuery: unknown, options?: {
  overrideMaxPaginationLimit?: number
}) {
  const safePaginationSchema = safeParser(paginationSchema)
  const parsedData = safePaginationSchema(reqQuery)
  const pagination = parsedData.success ? parsedData.output : parse(paginationSchema, {})
  const _limit = (pagination.limit ?? 10)
  const _page = (pagination.page ?? 1)

  const limit = options?.overrideMaxPaginationLimit ?? Math.min(_limit, MAX_PAGINATION_LIMIT)

  return {
    limit,
    page: _page,
    skip: (_page - 1) * limit,
    orderBy: {
      [pagination.sort_by]: pagination.order,
    },
  }
}

export function getPaginatedResponse<T>(
  data: T[],
  total: number,
  paginationParams: ReturnType<typeof getPaginationParams>,
) {
  return {
    data,
    meta: {
      total,
      page: paginationParams.page,
      limit: paginationParams.limit,
      hasPreviousPage: paginationParams.page > 1,
      hasNextPage: total > paginationParams.skip + data.length,
    },
  }
}

export interface PaginationResponse<T> {
  data: T[]
  meta: {
    page: number
    total: number
    limit: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export const paginationLimitOptions = [10, 15, 20, 50, 80]
